import { useQuery } from "@tanstack/react-query";
import type {
  ExtractedJobLocation,
  LocationFilter,
  ScoreAxes,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";

export interface JobRow {
  company_id: string;
  company_name: string;
  company_tier: number;
  job_id: string;
  url: string;
  title: string;
  location: string | null;
  department: string | null;
  updated_at: string | null;
  first_seen_at: number;
  last_seen_at: number;
  // Score (per current user, current primary resume). Null when the job
  // hasn't been scored yet.
  score: number | null;
  score_axes: ScoreAxes | null;
  score_rationale: string | null;
  // Structured location facts; null on extract miss.
  location_facts: ExtractedJobLocation | null;
  location_summary: string | null;
  location_eligible_for_user: boolean | null;
  // Posted annual USD pay (null when none is posted) and the est. 3-year
  // TTC built on it plus the company equity model.
  pay: JobPay | null;
  comp_estimate: CompEstimate | null;
}

export interface JobPay {
  min: number;
  max: number;
  currency: string;
  is_ote: boolean;
  has_commission: boolean;
  source: string;
  equity_offered: boolean;
  equity_cash_min: number | null;
  equity_cash_max: number | null;
}

export interface CompEstimate {
  low: number;
  typical: number;
  high: number;
  basis: string;
  confidence: "high" | "medium" | "low";
}

export function formatK(n: number): string {
  return `$${Math.round(n / 1000)}K`;
}

export interface JobsResponse {
  jobs: JobRow[];
  next_cursor: string | null;
  total_active: number;
  returned: number;
}

export interface ShortlistResponse {
  jobs: JobRow[];
  returned: number;
}

// Single-job hydration for surfaces that already know the (companyId,
// jobId) composite — e.g. the resume-tailor workshop deep-links from
// /jobs/:c/:j/tailor or /applications/:id/resume.
export function useJobByIds(
  companyId: string | null,
  jobId: string | null,
) {
  return useQuery<{ job: JobRow }>({
    queryKey: ["jobs", "one", companyId, jobId],
    enabled: companyId !== null && jobId !== null,
    staleTime: 30_000,
    queryFn: () =>
      api<{ job: JobRow }>(
        `/api/jobs/${encodeURIComponent(companyId!)}/${encodeURIComponent(jobId!)}`,
      ),
  });
}

export function useShortlist(n = 10) {
  return useQuery({
    queryKey: ["shortlist", n],
    queryFn: () => api<ShortlistResponse>(`/api/jobs/shortlist?n=${n}`),
    staleTime: 30_000,
  });
}

export interface SimilarResponse {
  jobs: JobRow[];
  returned: number;
}

export type SimilarError = "not_embedded" | "unknown";

export function useSimilarJobs(
  companyId: string | null,
  jobId: string | null,
  n = 10,
) {
  return useQuery<SimilarResponse, SimilarError>({
    queryKey: ["similar", companyId, jobId, n],
    enabled: companyId !== null && jobId !== null,
    staleTime: 60_000,
    queryFn: async () => {
      const url = `/api/jobs/${encodeURIComponent(companyId!)}/${encodeURIComponent(jobId!)}/similar?n=${n}`;
      try {
        return await api<SimilarResponse>(url);
      } catch (e) {
        // The route returns 404 with `{error:"not_embedded"}` when the
        // source job isn't in Vectorize yet — surface that distinctly so
        // the UI can render a useful empty state instead of a generic error.
        if (e instanceof ApiError && e.status === 404) {
          throw "not_embedded" as SimilarError;
        }
        throw "unknown" as SimilarError;
      }
    },
  });
}

export interface JobsFilters {
  company?: string;
  tier?: 1 | 2 | 3;
  // Legacy classifyLocation path. Still accepted by /api/jobs; the new UI
  // doesn't surface controls for these but we keep the fields so saved
  // bookmarks / programmatic callers don't break.
  remote?: boolean;
  usOnly?: boolean;
  states?: string[];
  search?: string;
  maxAgeDays?: number;
  // Structured location filter (mode, eligible_in, max_in_office_days, ...).
  // Backed by the LocationFilter sidebar component.
  location?: LocationFilter;
  sort?: "score_desc" | "created_desc";
  limit?: number;
  cursor?: string;
}

export function useJobs(filters: JobsFilters) {
  const qs = filtersToQuery(filters);
  return useQuery({
    queryKey: ["jobs", qs],
    queryFn: () => api<JobsResponse>(`/api/jobs${qs ? `?${qs}` : ""}`),
    staleTime: 30_000,
  });
}

export function filtersToQuery(filters: JobsFilters): string {
  const sp = new URLSearchParams();
  if (filters.company) sp.set("company", filters.company);
  if (filters.tier) sp.set("tier", String(filters.tier));
  if (filters.remote) sp.set("remote", "1");
  if (filters.usOnly) sp.set("us_only", "1");
  if (filters.states?.length) sp.set("state", filters.states.join(","));
  if (filters.maxAgeDays) sp.set("max_age", String(filters.maxAgeDays));
  if (filters.search) sp.set("q", filters.search);
  if (filters.location) {
    const l = filters.location;
    if (l.mode?.length) sp.set("mode", l.mode.join(","));
    if (l.eligible_in) sp.set("eligible_in", l.eligible_in);
    if (l.excludes_state) sp.set("excludes_state", l.excludes_state);
    if (typeof l.max_in_office_days === "number")
      sp.set("max_in_office_days", String(l.max_in_office_days));
    if (typeof l.max_travel_pct === "number")
      sp.set("max_travel_pct", String(l.max_travel_pct));
    if (l.requires_no_visa_sponsorship) sp.set("requires_no_visa_sponsorship", "1");
    if (l.requires_relocation_offered) sp.set("requires_relocation_offered", "1");
  }
  if (filters.sort) sp.set("sort", filters.sort);
  if (filters.limit) sp.set("limit", String(filters.limit));
  if (filters.cursor) sp.set("cursor", filters.cursor);
  return sp.toString();
}
